import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useT } from '../../i18n/I18nProvider';
import { GuideText } from './GuideText';
import { useGuides } from './GuideProvider';
import type { GuideLink } from './types';

/**
 * A small question mark beside a settings tab (8 October 2026): what this tab is for, in a few lines, with links
 * to a page or a how-to tour. It sits beside the tab, not inside it, so the tab stays one plain button for the
 * keyboard and screen readers.
 */
export function TabHelp({ guide, titleKey, bodyKey, links }: { guide?: string; titleKey: string; bodyKey: string; links?: GuideLink[] }) {
  const t = useT();
  const nav = useNavigate();
  const { startTour } = useGuides();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const ref = useRef<HTMLSpanElement>(null);
  const btn = useRef<HTMLButtonElement>(null);

  // The tab strip scrolls sideways and would cut a popup off, so it is placed on the window, under the mark.
  const toggle = () => {
    const r = btn.current?.getBoundingClientRect();
    if (r) {
      const width = Math.min(300, window.innerWidth - 32);
      setPos({ top: r.bottom + 8, left: Math.min(Math.max(16, r.left + r.width / 2 - width / 2), window.innerWidth - width - 16) });
    }
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const gone = () => setOpen(false);
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    window.addEventListener('scroll', gone, true);
    window.addEventListener('resize', gone);
    return () => {
      document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc);
      window.removeEventListener('scroll', gone, true); window.removeEventListener('resize', gone);
    };
  }, [open]);

  const title = t(titleKey);
  return (
    <span className="rr-tabhelp" ref={ref}>
      <button ref={btn} type="button" className="rr-tabhelp__button" aria-label={t('guide.tabHelp.label', { tab: title })} aria-expanded={open}
        title={t('guide.tabHelp.label', { tab: title })} onClick={toggle} data-testid={`tabhelp-${titleKey}`}>
        ?
      </button>
      {open && (
        <span className="rr-tabhelp__pop" role="dialog" aria-label={title} style={{ top: pos.top, left: pos.left }}>
          <strong>{title}</strong>
          <GuideText text={t(bodyKey)} />
          {links && links.length > 0 && (
            <span className="rr-guide__links">
              {links.map((l) => (
                <button key={(l.to ?? l.tour ?? '') + l.labelKey} type="button" className="rr-guide__link"
                  onClick={() => { setOpen(false); if (l.tour && guide) startTour(guide, l.tour); else if (l.to) nav(l.to); }}>
                  {t(l.labelKey)} →
                </button>
              ))}
            </span>
          )}
        </span>
      )}
    </span>
  );
}
